import React from 'react';
import { Button, Tooltip, type ButtonProps } from '@mui/material';
import { useWriteGuard } from '@/hooks/useWriteGuard';

/**
 * MUI Button that is automatically disabled when the current user has the
 * viewer (read-only) role.  Drop-in replacement for {@link Button} in any
 * component whose only write-guard need is disabling a submit/action button.
 *
 * When disabled due to the viewer role, a tooltip explains why the action is
 * unavailable (wrapping in a `<span>` is required for MUI Tooltip to work
 * on disabled elements).
 *
 * For guarding non-button UI (inputs, layout, conditional columns) use the
 * {@link useWriteGuard} hook directly instead.
 */
export const ViewerGuardedButton: React.FC<ButtonProps> = (props) => {
  const isViewer = useWriteGuard();
  const { disabled, ...rest } = props;
  const isDisabled = disabled || isViewer;

  if (isViewer) {
    return (
      <Tooltip title="You have read-only access" arrow>
        <span>
          <Button {...rest} disabled />
        </span>
      </Tooltip>
    );
  }

  return <Button {...rest} disabled={isDisabled} />;
};
